import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { useWinnersByMonth } from "@/hooks/use-query-hooks";
import { STATES } from "@/data/states";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// US-shaped grid layout of state abbreviations (rows ~ US geography)
const ROWS: string[][] = [
  ["AK", "", "", "", "", "", "", "", "", "ME"],
  ["", "", "", "", "WI", "", "", "", "VT", "NH"],
  ["WA", "ID", "MT", "ND", "MN", "IL", "MI", "NY", "MA", "CT"],
  ["OR", "NV", "WY", "SD", "IA", "IN", "OH", "PA", "NJ", "RI"],
  ["CA", "UT", "CO", "NE", "MO", "KY", "WV", "VA", "MD", "DE"],
  ["", "AZ", "NM", "KS", "AR", "TN", "NC", "SC", "DC", ""],
  ["", "", "", "OK", "LA", "MS", "AL", "GA", "", ""],
  ["HI", "", "", "TX", "", "", "", "FL", "", ""],
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function WinnerMap() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data: winners } = useWinnersByMonth(year, month);

  const winnersByState = useMemo(() => {
    const map = new Map<string, number>();
    (winners || []).forEach((w) => {
      const abbr = w.states?.abbreviation;
      if (!abbr) return;
      map.set(abbr, (map.get(abbr) || 0) + 1);
    });
    return map;
  }, [winners]);

  const stateBySlugAbbr = useMemo(() => {
    const m = new Map<string, { slug: string; name: string }>();
    STATES.forEach((s) => m.set(s.abbreviation, { slug: s.slug, name: s.name }));
    return m;
  }, []);

  const monthOptions: { value: string; label: string }[] = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      if (y === now.getFullYear() && m > now.getMonth() + 1) continue;
      monthOptions.push({ value: `${y}-${m}`, label: `${MONTHS[m - 1]} ${y}` });
    }
  }

  const totalWinners = winners?.length || 0;

  return (
    <section className="container py-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="font-display text-xl md:text-2xl font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-accent" />
          Winner Map
        </h2>
        <Select
          value={`${year}-${month}`}
          onValueChange={(v) => {
            const [y, m] = v.split("-").map(Number);
            setYear(y);
            setMonth(m);
          }}
        >
          <SelectTrigger className="w-[180px]" aria-label="Select month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        {ROWS.map((row, ri) => (
          <div
            key={ri}
            className="grid gap-1.5"
            style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}
          >
            {row.map((abbr, ci) => {
              if (!abbr) return <div key={ci} />;
              const info = stateBySlugAbbr.get(abbr);
              const count = winnersByState.get(abbr) || 0;
              const hasWinner = count > 0;
              const inner = (
                <div
                  className={`rounded-full border px-2 py-1 text-center text-xs font-semibold transition-colors ${
                    hasWinner
                      ? "bg-accent text-accent-foreground border-accent hover:opacity-90"
                      : "bg-card text-muted-foreground border-border hover:bg-secondary"
                  }`}
                  title={info ? `${info.name}${hasWinner ? ` — ${count} winner${count > 1 ? "s" : ""}` : ""}` : abbr}
                >
                  {abbr}{hasWinner ? ` · ${count}` : ""}
                </div>
              );
              return info ? (
                <Link key={ci} to="/states/$stateSlug" params={{ stateSlug: info.slug }}>
                  {inner}
                </Link>
              ) : (
                <div key={ci}>{inner}</div>
              );
            })}
          </div>
        ))}
      </div>

      {totalWinners === 0 && (
        <p className="text-center text-muted-foreground text-sm mt-6">
          No winner data available for this month yet.
        </p>
      )}
    </section>
  );
}